import {
  Banner,
  BlockStack,
  Button,
  ButtonProps,
  Card,
  Icon,
  InlineStack,
  LegacyStack,
  Spinner,
  Text,
} from '@shopify/polaris'
import { PlayIcon } from '@shopify/polaris-icons'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  adminDeepLink,
  ShowIntercomButton,
  useDialogActivator,
  useSystemData,
} from '../common/helpers'
import { SystemData } from '../common/SystemData'
import {
  mainThemeId,
  Theme,
  ThemeSelect,
  ThemeSettings,
  useMainThemeSettingsSWR,
  useThemesSWR,
  useThemeSWR,
  windowWithSelectedThemeIdCache,
} from '../common/themes'
import * as urls from '../common/urls'
import { VideoModal } from '../common/VideoModal'

export const themeProductEditAction = (themeId: number | string) =>
  window.open(adminDeepLink(`/themes/${themeId}/editor?template=product`), '_blank')

export const ThemeProductEditButton = ({
  themeId,
  ...buttonProps
}: { themeId: number | string } & ButtonProps) => {
  return (
    <Button {...buttonProps} onClick={() => themeProductEditAction(themeId)}>
      {buttonProps.children}
    </Button>
  )
}

export const appBlockInstallAction = (themeId: number | string, systemData?: SystemData) =>
  window.open(
    adminDeepLink(
      `/themes/${themeId}/editor?template=product&addAppBlockId=${systemData?.shopifyThemeAppExtensionId}/paywhirl-plan-selector&target=mainSection`
    ),
    '_blank'
  )

export const WidgetAppBlockInstallButton = ({
  themeId,
  themeSupportsAppBlock,
  onAddWidgetClick,
  ...buttonProps
}: {
  themeId: number | string
  themeSupportsAppBlock: boolean
  onAddWidgetClick?: () => void
} & ButtonProps) => {
  const systemData = useSystemData()
  const history = useHistory()

  const legacyInstallUrl = urls.legacyPlanSelectorInstallUrl({
    back_url: history.location.pathname,
  })

  return (
    <Button
      {...buttonProps}
      onClick={() => {
        onAddWidgetClick?.()
        if (themeSupportsAppBlock) {
          appBlockInstallAction(themeId, systemData)
        } else {
          history.push(legacyInstallUrl)
        }
      }}
    >
      {buttonProps.children}
    </Button>
  )
}

export const InstallSubscriptionWidgetLoadedContent = ({
  themes,
  onAddWidgetClick,
}: {
  themes: Theme[]
  onAddWidgetClick?: () => void
}) => {
  const initialThemeId = windowWithSelectedThemeIdCache.paywhirlSelectedThemeId
  const initialTheme = themes.find((t) => t.id === initialThemeId) ?? themes[0]

  const [selectedTheme, setSelectedTheme] = useState<Theme>(initialTheme)

  const { data: themeDetails } = useThemeSWR(selectedTheme?.id ?? null)

  const themeSupportsAppBlock = !themeDetails || themeDetails.supports_plan_selector_block !== false

  return (
    <InlineStack gap="400" wrap={false} blockAlign="end">
      <LegacyStack.Item fill>
        <ThemeSelect
          themes={themes}
          onChange={setSelectedTheme}
          value={selectedTheme}
          label="Select theme"
        />
      </LegacyStack.Item>
      <div style={{ flexShrink: 0 }}>
        <WidgetAppBlockInstallButton
          themeId={selectedTheme.id}
          themeSupportsAppBlock={themeSupportsAppBlock}
          onAddWidgetClick={onAddWidgetClick}
          disabled={!themeDetails}
          loading={!themeDetails}
          variant="primary"
          size="large"
        >
          Add{'  '}widget
        </WidgetAppBlockInstallButton>
      </div>
    </InlineStack>
  )
}

export const InstallSubscriptionWidgetContent = ({
  onAddWidgetClick,
}: {
  onAddWidgetClick?: () => void
}) => {
  const { data: themes } = useThemesSWR()

  if (themes === undefined) {
    return (
      <LegacyStack distribution="center">
        <Spinner size="small" />
      </LegacyStack>
    )
  }

  if (themes.length === 0) {
    return (
      <div>
        <Text as="span" tone="critical">
          No themes found in the store, please contact our support.
        </Text>
      </div>
    )
  }

  return (
    <InstallSubscriptionWidgetLoadedContent themes={themes} onAddWidgetClick={onAddWidgetClick} />
  )
}

export const SubscriptionWidgetStatusCard = ({ settings }: { settings: ThemeSettings }) => {
  const systemData = useSystemData()

  if (settings.subscriptionWidgetInstalled) {
    return (
      <Banner
        tone="success"
        action={{
          content: 'Edit settings',
          onAction: () => themeProductEditAction(mainThemeId(settings)),
        }}
      >
        <Text as="p">Subscription widget successfully installed in the main theme</Text>
      </Banner>
    )
  }

  if (!settings.themeSupportsAppBlocks) {
    return (
      <Banner tone="warning">
        <Text as="p">Subscription app block widget not supported in the main theme</Text>
      </Banner>
    )
  }

  // Widget not installed
  return (
    <Banner
      tone="warning"
      action={{
        content: 'Install now',
        onAction: () => appBlockInstallAction(mainThemeId(settings), systemData),
      }}
    >
      <Text as="p">Subscription widget not installed in the main theme</Text>
    </Banner>
  )
}

export const SubscriptionWidgetCard = () => {
  const videoActivator = useDialogActivator()
  const { data: mainThemeSettings } = useMainThemeSettingsSWR()

  return (
    <Card>
      {!mainThemeSettings && (
        <InlineStack align="center">
          <Spinner size="small" />
        </InlineStack>
      )}
      {mainThemeSettings && (
        <BlockStack gap="600">
          <SubscriptionWidgetStatusCard settings={mainThemeSettings} />
          <InstallSubscriptionWidgetContent />
          <InlineStack gap="200">
            <Button url={urls.widgetTranslationsUrl()}>Manage translations</Button>
            <Button onClick={videoActivator.show} icon={<Icon source={PlayIcon} tone="base" />}>
              Watch video
            </Button>
            <ShowIntercomButton
              message="I'd like help setting up PayWhirl with my theme."
              variant="plain"
            >
              Chat with us
            </ShowIntercomButton>
          </InlineStack>
        </BlockStack>
      )}
      <VideoModal
        src="https://www.youtube.com/embed/_3T-2NMNxvs"
        title="How To Install The PayWhirl Subscription Widget"
        activator={videoActivator}
      />
    </Card>
  )
}
